// src/components/careers/JobList.tsx
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useTranslation } from 'react-i18next';
import type { Job } from "@/types/job";

interface JobListProps {
  jobs: Job[];
  loading: boolean;
  error: string | null;
  onApply: (job: Job) => void;
  onClose: () => void;
}

/*  EXPORTED COMPONENT  */
export function JobList({
  jobs,
  loading,
  error,
  onApply,
  onClose,
}: JobListProps) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-8 space-y-2">
        <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
        <p className="text-slate-600">{t('careers.jobList.loading')}</p>
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-8 text-red-600">{error}</div>;
  }

  if (jobs.length === 0) {
    return (
      <div className="text-center py-8 text-slate-600">
        {t('careers.jobList.noPositions')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {jobs.map((job) => (
        <div
          key={job.id}
          className="border rounded-lg p-5 hover:shadow-md transition-shadow"
        >
          <div className="flex justify-between items-start mb-3">
            <div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white">
                {job.title}
              </h3>
              <div className="flex gap-4 text-sm text-slate-600 dark:text-slate-400 mt-1">
                <span>{job.type}</span>
                <span>{job.location}</span>
                <span className="text-green-600 font-medium">
                  {job.positions}{' '}
                  {t(job.positions === 1 ? 'careers.jobList.position' : 'careers.jobList.positions')}
                </span>
              </div>
            </div>
            <Button onClick={() => onApply(job)} size="sm">
              {t('careers.jobList.applyNow')}
            </Button>
          </div>
          <p className="text-slate-700 dark:text-slate-300 text-sm">
            {job.description}
          </p>
        </div>
      ))}
      <Button variant="outline" className="w-full" onClick={onClose}>
        {t('careers.jobList.close')}
      </Button>
    </div>
  );
}